import React, { FC, useMemo } from 'react';
import { DropdownMenu, Icon, IconButton } from '@kontentino/ui';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import AnalyticsExportsApi from 'api/analytics/exports';
import { useInsightsContainer } from 'components/insights/context';
import DateUtils from 'app/utils/date';
import { ANALYTICS_LITE_PAGE_PERFORMANCE } from '../utils/pagePerformanceMetrics';
import { ANALYTICS_LITE_POST_PERFORMANCE } from '../utils/postsPerformanceMetrics';
import { getMetricsProperty } from 'app/modules/insights/modules/pagePerformance/utils/metricProperty';
import { getMetricsForPageType } from 'app/modules/insights/modules/postsPerformance/utils/metricProperty';

const InsightLiteExportDropdown: FC = () => {
  const { filterState, activeState } = useInsightsContainer();
  const { t } = useTranslation();

  const litePageMetrics = useMemo(() => {
    const platformMetrics = activeState.page
      ? ANALYTICS_LITE_PAGE_PERFORMANCE[activeState.page._type] || []
      : [];
    return platformMetrics.map((metric) =>
      getMetricsProperty(activeState.page?._type ?? 0, metric.key),
    );
  }, [activeState.page]);

  const litePostMetrics = useMemo(() => {
    const platformMetrics = activeState.page
      ? ANALYTICS_LITE_POST_PERFORMANCE[activeState.page._type] || []
      : [];
    return platformMetrics.map(
      (metric) =>
        getMetricsForPageType(activeState.page?._type ?? 0)[metric.key],
    );
  }, [activeState.page]);

  const { mutate: mutatePDFExport } = useMutation<Window | null, null, void>(
    () => {
      if (activeState.page) {
        return AnalyticsExportsApi.exportPDFInsightsLite({
          startDate: DateUtils.toDateString(filterState.startDate),
          endDate: DateUtils.toDateString(filterState.endDate),
          types: filterState.selectedPostTypes,
          metric: litePageMetrics[0], // Use first metric as default
          page: activeState.page,
          postMetrics: litePostMetrics,
        });
      }

      return Promise.reject();
    },
  );

  if (!activeState.page) {
    return null;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          icon={<Icon icon={faArrowDownToLine} />}
          variant="secondary"
          className="tw-h-[36px]"
          disabled={!activeState.page || litePageMetrics.length === 0}
          data-name="insights-lite-download"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="tw-z-10 tw-min-w-[240px]">
        <DropdownMenu.Item
          onClick={() => mutatePDFExport()}
          data-name="insights-lite-download-current-selection_pdf"
        >
          <Icon icon={faArrowDownToLine} />
          {t('exportAs', { type: 'PDF' })}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default InsightLiteExportDropdown;
